<template>
    <Layout :loading="!blocks?.length || (!layout && !generalError.length)">
        <template #hoisted>
            <div class="blocks-hoisted">
                <BlockBuilder
                    v-if="layout"
                    :key="layout?.id"
                    :blocks="hoistedBlocks"
                    hoisted
                    :loading="loading"
                    :custom-page-id="layout?.id"
                    :conditions-initialized="conditionsInitialized"
                    @update-block="handleBlockUpdate"
                    @render:error="(e) => showError({ error: e, retry: true })"
                />
            </div>
        </template>

        <form
            class="blocks-wrapper"
            :class="{ preview: widgetOptions.customPagePreview }"
            @submit="$event.preventDefault()"
        >
            <BlockBuilder
                v-if="layout"
                :key="layout?.id"
                :blocks="blocks"
                :loading="loading"
                :custom-page-id="layout?.id"
                :conditions-initialized="conditionsInitialized"
                @update-block="handleBlockUpdate"
                @render:error="(e) => showError({ error: e, retry: true })"
            />
            <ErrorText
                v-if="generalError.length"
                :error="generalError"
            />
        </form>
    </Layout>
</template>

<script lang="ts" setup>
import { clearBlockErrors, decompressLayoutDescriptor, isGridLayoutProvided, preparePayload } from './functions'
import useApi from '@/views/apps/_customPage/api'
import { computed, onBeforeMount, onMounted, onUnmounted, provide, ref, watchEffect } from 'vue'
import useTracker from '@/composables/tracker'
import BlockBuilder from '@/views/apps/_customPage/components/BlockBuilder.vue'
import type { Error } from '@/views/apps/_customPage/types'
import useWidgetViewManager from '@/composables/widget-view-manager'
import Layout from '@/components/Layout.vue'
import type { CustomPageLayout, Message } from '@/types'
import useWidgetOptions from '@/composables/widget-options'
import components, {
    type ApiCustomPageComponents,
    type ContainerApi,
    type CustomCssConfig,
    type CustomCssVariables,
    customPageBlockMap,
    type ExtendedBlock,
} from 'widget-components'
import { validateBlock } from './functions/validate-user-input'
import { handleComponentErrors } from '@/views/apps/_customPage/functions/handle-component-errors'
import { path } from 'ramda'
import { useEventBus } from '@vueuse/core'
import useQuickJsContext from '@/composables/quickjs/wasm-context'
import { blocks, flattenedBlocks, setBlocks, updateBlock } from 'widget-components/integrations/CustomPage/blocks-state'
import type {
    BlockBuilderUpdateBlockByPathPayload,
} from 'widget-components/integrations/CustomPage/utilities/emitBlockUpdate'
import { isAxiosError } from 'axios'
import { CustomPageBlockError } from '@/views/apps/_customPage/errors'
import useConditionsManager from '@/views/apps/_customPage/composables/useConditionsManager/useConditionsManager'
import { captureException, logger } from '@sentry/vue'

const { ErrorText } = components

defineOptions({ inheritAttrs: false })

const {
    getCssVariables,
    applyCssVariables,
} = components.useCustomCss()

const TRACKER_APP_NAME = 'customPage'

const { sendAction, postCustomPageValues } = useApi()
const { track } = useTracker(TRACKER_APP_NAME)
const { finishAppView, activeMessage, currentViewOptions, showError } = useWidgetViewManager()
const { options: widgetOptions } = useWidgetOptions()

const loading = ref(false)

const layout = ref<CustomPageLayout | null>(null)
const header = ref('')
const title = ref('')

const gridSystemEnabled = ref(false)
const generalError = ref('')

const hoistedBlocks = computed(() => flattenedBlocks.value.filter(b => b.type === 'NAVIGATION'))

const {
    init: initConditions,
    initialized: conditionsInitialized,
    assignConditions,
} = useConditionsManager(
    (e: unknown) => {
        showError({ error: e, retry: true })
        captureException(e, (scope) => {
            scope.setExtra('custom_page_id', layout.value?.id)
            return scope
        })
    },
)

const handleErrors = (errors: Array<Error>) => {
    let handled = false

    const componentSpecificErrors = errors.filter((error) => !!error.componentIndex)
    if (componentSpecificErrors.length) {
        handled ||= handleComponentErrors(flattenedBlocks, componentSpecificErrors)
    }

    const generalErrors = errors.filter((error) => !error.componentIndex)
    if (generalErrors.length) {
        generalError.value = generalErrors.map(e => e.message || e.code).join(', ')
        handled = true
    }

    return handled
}

const dispatchCpEvent = (actionName: string, props = []) => {
    track(actionName, TRACKER_APP_NAME, {
        userDefinedName: title.value,
        ...props,
    })
}

const sendTrackerEvents = (trackers: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
    trackers
        .forEach((data: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
            dispatchCpEvent(data.actionName, data.props)
        })
}

const handleTracker = (data: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
    dispatchCpEvent(data.actionName, data.props)
}

provide('handleTracker', handleTracker)

const handleAction = async (action: ExtendedBlock | undefined) => {
    const { options } = useWidgetOptions()

    if (!action || loading.value || options.value.customPagePreview) {
        return
    }

    loading.value = true

    try {
        generalError.value = ''
        const message = activeMessage.value as Message

        clearBlockErrors(blocks.value)
        const { trackers, containerValues, componentValues } = await preparePayload(blocks.value, action)

        const payload = {
            id: layout.value?.id,
            containerValues,
            componentValues,
        }

        if (action.type === 'ACTION_BUTTON') {
            sendTrackerEvents(trackers)
        }

        if (message.taskType === 'CUSTOM_PAGE_2') {
            await postCustomPageValues(message.workflowId as string, message.taskId as string, payload)
        } else {
            await sendAction(message.workflowId as string, message.taskId as string, payload)
        }

        await finishAppView({
            track,
            loader: false,
        })
    } catch (e: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
        const errors = e.response?.data?.errors || []
        const status = e.response?.status || 0
        if (
            e instanceof CustomPageBlockError ||
            handleErrors(errors) ||
            (isAxiosError(e) && status < 500)
        ) {
            console.warn(e) // eslint-disable-line no-console
        } else {
            console.error(e) // eslint-disable-line no-console
        }
    } finally {
        loading.value = false
    }
}

provide('handleAction', handleAction)

const applyStyles = (opts: {
    config?: CustomCssConfig
    variables: CustomCssVariables | undefined
} | undefined) => {
    const el = document.querySelector('.widget-card-layout') as HTMLElement | undefined || document.body
    const { variables, config } = opts || {}

    if (Object.keys(variables || {}).length) {
        applyCssVariables(variables || {}, el)
        return
    }

    applyCssVariables(getCssVariables(config as CustomCssConfig), el)
}

const composeContent = async () => {
    const { customPagePreviewData } = currentViewOptions.value

    let layoutDescriptor: CustomPageLayout = {
        components: [],
        containers: [],
        id: '',
        title: '',
        header: '',
        style: {},
    }

    if (customPagePreviewData) {
        const { components, style, cssVariables } = JSON.parse(customPagePreviewData)
        setBlocks(customPageBlockMap(components))
        layout.value = {
            ...layoutDescriptor,
            components,
        }
        gridSystemEnabled.value = isGridLayoutProvided(blocks.value)
        applyStyles({
            config: style,
            variables: cssVariables,
        })
        return
    }

    const conduct = activeMessage.value
    if (!conduct) return

    if (typeof conduct.layoutDescriptor === 'string') {
        layoutDescriptor = JSON.parse(conduct.layoutDescriptor as string)
    }

    if (typeof conduct.layoutDescriptor === 'object') {
        layoutDescriptor = await decompressLayoutDescriptor()
    }

    layout.value = layoutDescriptor

    window.layoutDescriptor = layoutDescriptor

    const mergedComponents = [
        ...layout.value.components ? layout.value.components : [],
        ...layout.value.containers ? layout.value.containers : [],
    ] as ApiCustomPageComponents[]

    if (conduct.taskType === 'CUSTOM_PAGE_2' && layoutDescriptor.root) {
        mergedComponents.unshift(layoutDescriptor.root as ContainerApi)
    }

    setBlocks(customPageBlockMap(mergedComponents))
    header.value = layoutDescriptor.header || ''
    title.value = layoutDescriptor.title

    gridSystemEnabled.value = isGridLayoutProvided(blocks.value)

    applyStyles({
        config: layout.value.style as CustomCssConfig,
        variables: layout.value.cssVariables as CustomCssVariables,
    })

    initConditions(layout.value.importedValues)

    logger.info('Custom Page content composed')
}

const handlePreviewUpdate = (customPagePreviewData: string) => {
    const { components, style, cssVariables } = JSON.parse(customPagePreviewData)
    setBlocks(customPageBlockMap(components))
    Object.assign(layout.value!, {
        components,
        id: `${ +new Date() }`,
    })
    applyStyles({
        config: style as CustomCssConfig,
        variables: cssVariables as CustomCssVariables,
    })
}

const getWasmContext = useQuickJsContext()

onMounted(async () => {
    await Promise.all([getWasmContext(), composeContent()]) // parallel
    dispatchCpEvent('screenView')
})

onBeforeMount(() => {
    useEventBus<BlockBuilderUpdateBlockByPathPayload>('update-block-by-path').on(handleBlockUpdateByPath)
    useEventBus<string>('update-custom-page-preview').on(handlePreviewUpdate)
})

onUnmounted(() => {
    if (window.layoutDescriptor) {
        delete window.layoutDescriptor
    }
    useEventBus<BlockBuilderUpdateBlockByPathPayload>('update-block-by-path').off(handleBlockUpdateByPath)
    useEventBus<string>('update-custom-page-preview').off(handlePreviewUpdate)
})

const handleBlockUpdate = (id: string, value: ExtendedBlock) => {
    const blockIndex = blocks.value.findIndex((block) => block.id === id)
    const newBlocks = [...blocks.value]

    if (blockIndex > -1) {
        newBlocks[blockIndex] = value
        setBlocks(newBlocks)
    }

    assignConditions()
}

const handleBlockUpdateByPath = (val: BlockBuilderUpdateBlockByPathPayload) => {
    const block = path(val.path, blocks.value) as ExtendedBlock

    const newBlock = {
        ...block,
        ...val.block,
        error: undefined,
    } as ExtendedBlock

    if ('currentValue' in val.block) {
        validateBlock(newBlock, true)
    }

    updateBlock(newBlock)
}

// TODO get rid of it - move to specific components/emits/provides?
// Form blocks watch for dynamic component interactions
watchEffect(() => {
    const docUploads = blocks.value.filter(el => ['INPUT_DOCUMENT', 'INPUT_FILE'].includes(el.type))

    if (docUploads.length) {
        const hasDocError = (docUploads).reduce((res, du) => {
            const hasError = du.currentValue?.document?.error?.length > 0 || !!du.error
            return res || hasError
        }, false)

        setBlocks(blocks.value.map(block => {
            if (!['ACTION_BUTTON', 'NAVIGATION', 'SELECTION_LIST'].includes(block.type)) {
                return block
            }

            return {
                ...block,
                disabled: hasDocError,
            }
        }))
    }
})
</script>

<style lang="scss">
@import '@/styles/typography.scss';

// temp solution to make preview of custom page 1:1 with edit mode in dashboard
.preview {
    padding-top: 1px;
}

.consent {
    a {
        @extend .text-link-small
    }
}

.blocks-wrapper {
    height: 100%;
}
</style>
